/**
 * After a deploy the previous build's hashed files are gone. A tab still
 * running the old build fails to load the next page it opens ("Failed to
 * fetch dynamically imported module …"). Reloading picks up the new build.
 *
 * Reload at most once per short window so a genuinely broken file can never
 * cause a reload loop.
 */

const KEY = 'orlenn:chunk-reload-at';
const WINDOW_MS = 30_000;

const CHUNK_ERROR =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|loading (css )?chunk \S+ failed/i;

/** True for "this build's file is missing" errors (a new deploy). */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: unknown }).name;
  if (name === 'ChunkLoadError') return true;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String((error as { message?: unknown }).message ?? '');
  return CHUNK_ERROR.test(message);
}

function readLast(storage: Storage | null): number {
  try {
    return Number(storage?.getItem(KEY) ?? 0) || 0;
  } catch {
    return 0;
  }
}

/**
 * Reload the page to load the latest build — unless we already did so in
 * the last {@link WINDOW_MS}. Returns true when a reload was started.
 */
export function reloadForNewBuild(
  now: number = Date.now(),
  storage: Storage | null = safeSession(),
  reload: () => void = () => window.location.reload(),
): boolean {
  const last = readLast(storage);
  if (last > 0 && now - last < WINDOW_MS) return false;
  try {
    storage?.setItem(KEY, String(now));
  } catch {
    // Storage blocked: still reload once (no loop guard across reloads).
  }
  reload();
  return true;
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Vite fires `vite:preloadError` when a lazily loaded page (or its CSS)
 * cannot be fetched: reload into the new build instead of failing.
 */
export function installChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
}
